import { useTheme } from '@/contexts/ThemeContext'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { GFX_SLOT_ID, type GfxConfig } from '@/types'
import { getHorizontalBannerPlacement } from '@/utils/sponsorBannerPosition'
import './SponsorBanners.css'

import type { GfxState, ScoreBugPosition } from '@/services/jsonDataService'
import type { DISPLAY_STATE } from '@/types'
import type { FC } from 'react'

interface SponsorBannersProps {
  gfxConfig?: GfxConfig
  gfxState?: GfxState
  scoreBugPosition: ScoreBugPosition
  scorebugPanelHeight: number
  displayState: DISPLAY_STATE
}

// Horizontal: 800×150 at 1080p — scaled via baseHeight
const H_WIDTH_MULTIPLIER = 16
const H_HEIGHT_MULTIPLIER = 3

// Vertical: 150×800 at 1080p
const V_WIDTH_MULTIPLIER = 3
const V_HEIGHT_MULTIPLIER = 16

const HorizontalBanner: FC<{
  imageUrl: string
  visible: boolean
  scoreBugPosition: ScoreBugPosition
  scorebugPanelHeight: number
  displayState: DISPLAY_STATE
}> = ({ imageUrl, visible, scoreBugPosition, scorebugPanelHeight, displayState }) => {
  const theme = useTheme()
  // GSAP ref is on the inner element so its transform doesn't fight the
  // outer wrapper's CSS positioning (e.g. translateX(-50%) for centred cases)
  const ref = useTemplateAnimation(visible, { animationType: 'slideUp', duration: 0.5 })
  const { wrapperClass, wrapperStyle } = getHorizontalBannerPlacement(
    scoreBugPosition,
    scorebugPanelHeight,
    displayState
  )

  return (
    <div className={`sponsor-banner sponsor-banner--horizontal ${wrapperClass}`} style={wrapperStyle}>
      <div ref={ref}>
        <img
          src={imageUrl}
          alt="Sponsor"
          style={{
            width: `calc(${theme.baseHeight} * ${H_WIDTH_MULTIPLIER})`,
            height: `calc(${theme.baseHeight} * ${H_HEIGHT_MULTIPLIER})`,
            display: 'block',
          }}
        />
      </div>
    </div>
  )
}

const VerticalBanners: FC<{ imageUrl: string; visible: boolean }> = ({ imageUrl, visible }) => {
  const theme = useTheme()
  // Outer wrappers hold the CSS transform for vertical centering.
  // GSAP refs are on inner divs so animations don't clobber translateY(-50%).
  const leftRef = useTemplateAnimation(visible, { animationType: 'slideRight', duration: 0.5 })
  const rightRef = useTemplateAnimation(visible, { animationType: 'slideLeft', duration: 0.5 })

  const imgStyle = {
    width: `calc(${theme.baseHeight} * ${V_WIDTH_MULTIPLIER})`,
    height: `calc(${theme.baseHeight} * ${V_HEIGHT_MULTIPLIER})`,
    display: 'block',
  }

  return (
    <>
      <div className="sponsor-banner sponsor-banner--vertical-left">
        <div ref={leftRef}>
          <img src={imageUrl} alt="Sponsor" style={imgStyle} />
        </div>
      </div>
      <div className="sponsor-banner sponsor-banner--vertical-right">
        <div ref={rightRef}>
          <img src={imageUrl} alt="Sponsor" style={imgStyle} />
        </div>
      </div>
    </>
  )
}

const SponsorBanners: FC<SponsorBannersProps> = ({
  gfxConfig,
  gfxState,
  scoreBugPosition,
  scorebugPanelHeight,
  displayState,
}) => {
  const slots = gfxConfig?.imageSlots ?? []
  const horizontalUrl = slots.find((s) => s.slotID === GFX_SLOT_ID.HORIZONTAL_BANNER)?.url
  const verticalUrl = slots.find((s) => s.slotID === GFX_SLOT_ID.VERTICAL_BANNER)?.url

  const horizontalVisible = gfxState?.[GFX_SLOT_ID.HORIZONTAL_BANNER]?.visible ?? false
  const verticalVisible = gfxState?.[GFX_SLOT_ID.VERTICAL_BANNER]?.visible ?? false

  return (
    <>
      {horizontalUrl && (
        <HorizontalBanner
          imageUrl={horizontalUrl}
          visible={horizontalVisible}
          scoreBugPosition={scoreBugPosition}
          scorebugPanelHeight={scorebugPanelHeight}
          displayState={displayState}
        />
      )}
      {verticalUrl && <VerticalBanners imageUrl={verticalUrl} visible={verticalVisible} />}
    </>
  )
}

export default SponsorBanners
